'use client';
import { useMutation } from 'convex/react';
import { useQueryWithStatus } from '@/lib/convex';
import { useEffect, useRef } from 'react';
import { useLocale } from 'next-intl';
import { toast } from 'sonner';
import { api } from '@/convex/_generated/api';
import type { Id } from '@/convex/_generated/dataModel';
import {
  getMessagingChannelsCopy,
  formatMessagingCopy,
} from '@/i18n/messaging-channels-copy';
import { MESSAGING_CHANNEL_NAMES } from './messaging-ui-state';

/** Persistent owner confirmation, including links made while the app was closed. */
export function useMessagingLinkNotices(
  organizationId: Id<'organizations'> | undefined
) {
  const notices = useQueryWithStatus(
    api.messaging.identities.listLinkNotices,
    organizationId ? { organizationId } : 'skip'
  ).data;
  const acknowledge = useMutation(
    api.messaging.identities.acknowledgeLinkNotice
  );
  const unlink = useMutation(api.messaging.identities.unlink);
  const copy = getMessagingChannelsCopy(useLocale());
  const shown = useRef(new Set<Id<'messagingIdentities'>>());
  useEffect(
    () => () => {
      for (const id of shown.current) toast.dismiss(`messaging-linked:${id}`);
      shown.current.clear();
    },
    [organizationId]
  );
  useEffect(() => {
    const current = new Set((notices ?? []).map((notice) => notice.identityId));
    for (const id of shown.current)
      if (!current.has(id)) {
        toast.dismiss(`messaging-linked:${id}`);
        shown.current.delete(id);
      }
    for (const notice of notices ?? []) {
      if (shown.current.has(notice.identityId)) continue;
      shown.current.add(notice.identityId);
      const channel = `${MESSAGING_CHANNEL_NAMES[notice.platform]} ${notice.account}`;
      toast.success(formatMessagingCopy(copy.toastConnected, { channel }), {
        id: `messaging-linked:${notice.identityId}`,
        duration: Infinity,
        closeButton: true,
        onDismiss: () => {
          void acknowledge({ identityId: notice.identityId }).catch(() => {});
        },
        action: {
          label: copy.disconnect,
          onClick: () => {
            void unlink({ identityId: notice.identityId }).catch(() =>
              toast.error(
                formatMessagingCopy(copy.disconnectError, { channel })
              )
            );
          },
        },
      });
    }
  }, [notices, copy, acknowledge, unlink]);
}
